import { Button } from "primereact/button";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import { useContext, useEffect } from "react";
import { useLazyLoadQuery, useMutation, usePaginationFragment } from "react-relay";
import { IdDisplayField } from "@components/id-display-field";
import { useHasPermissions } from "@hooks/use-has-permissions";
import { useTranslation } from "@i18n/hooks";
import { type licenseDefinitionsTable_DeleteLicenseDefinitionAdminMutation } from "@relay/licenseDefinitionsTable_DeleteLicenseDefinitionAdminMutation.graphql";
import { type licenseDefinitionsTable_LicenseDefinitionsFragment$key } from "@relay/licenseDefinitionsTable_LicenseDefinitionsFragment.graphql";
import { type licenseDefinitionsTable_Query } from "@relay/licenseDefinitionsTable_Query.graphql";
import { type LicenseDefinitionsTable_Refetch } from "@relay/LicenseDefinitionsTable_Refetch.graphql";
import {
	DELETE_LICENSE_DEFINITION_MUTATION,
	LICENSE_DEFINITIONS_FRAGMENT,
	QUERY,
} from "./license-definitions-table.graphql";
import { DataTable } from "../../../components/data-table";
import { LicenseDefinitionsScreenContext } from "../../../screens/license-definitions/license-defintions.context";
import { EditFlexLicenseDefinitionButton } from "../edit-flex-license-definition-button";
import { LicenseDefinitionsTableFilter } from "../license-definitions-table-filter";

export const LicenseDefinitionsTable = () => {
	const { setConnectionId } = useContext(LicenseDefinitionsScreenContext);
	const { t } = useTranslation("common");
	const data = useLazyLoadQuery<licenseDefinitionsTable_Query>(
		QUERY,
		{ first: 20 },
		{ fetchPolicy: "network-only" },
	);

	const {
		hasNext,
		loadNext,
		data: {
			Admin: {
				LicenseDefinition: { SearchLicenseDefinitions },
			},
		},
		refetch,
	} = usePaginationFragment<
		LicenseDefinitionsTable_Refetch,
		licenseDefinitionsTable_LicenseDefinitionsFragment$key
	>(LICENSE_DEFINITIONS_FRAGMENT, data);

	useEffect(() => {
		setConnectionId(SearchLicenseDefinitions.__id);
	}, [SearchLicenseDefinitions.__id]);

	const [deleteLicenseDefinition] =
		useMutation<licenseDefinitionsTable_DeleteLicenseDefinitionAdminMutation>(
			DELETE_LICENSE_DEFINITION_MUTATION,
		);

	const licenseDefinitions = SearchLicenseDefinitions.edges;

	const handleDeleteLicenseDefinitionOnClick = (id: string) => {
		confirmDialog({
			header: t("license.removeDefinition"),
			message: t("license.removeDefinitionConfirm"),
			icon: "pi pi-info-circle",
			acceptClassName: "p-button-danger",
			acceptLabel: t("actions.yes"),
			rejectLabel: t("actions.no"),
			accept: () => {
				deleteLicenseDefinition({
					variables: {
						input: {
							licenseDefinitionId: id,
						},
						connections: [SearchLicenseDefinitions.__id],
					},
				});
			},
		});
	};

	const handleLoadMore = () => {
		loadNext(20);
	};

	const canModify = useHasPermissions(["UserInAccountPermission_LicenseAdmin_Modify"]);

	return (
		<>
			<div className="mb-5">
				<DataTable
					emptyMessage={
						<div className="flex justify-content-center align-items-center">
							<div className="mr-2">{t("license.noDefinitionsFound")}</div>
						</div>
					}
					header={
						<LicenseDefinitionsTableFilter
							refetch={(name?: string) =>
								refetch(
									{ nameMatchRegex: name || null },
									{ fetchPolicy: "network-only" },
								)
							}
						/>
					}
					className="mb-3"
					value={licenseDefinitions?.map((edge) => edge!.node!)}
				>
					{(Column) => (
						<>
							<Column
								header={t("table.id")}
								style={{ width: "5%" }}
								body={(licenseDefinition) => (
									<IdDisplayField id={licenseDefinition.id} />
								)}
							/>
							<Column header={t("table.name")} field="data.name" />
							{canModify && (
								<Column
									header={t("table.actions")}
									style={{ width: "10%" }}
									body={(licenseDefinition) => {
										return (
											<div>
												{licenseDefinition.data.kind === "Flex" && (
													<EditFlexLicenseDefinitionButton
														licenseDefinitionFragmentRef={
															licenseDefinition
														}
													/>
												)}

												<Button
													className="ml-2"
													onClick={() => {
														handleDeleteLicenseDefinitionOnClick(
															licenseDefinition.id,
														);
													}}
													icon={"pi pi-trash"}
												/>
											</div>
										);
									}}
								/>
							)}
						</>
					)}
				</DataTable>
				{hasNext && (
					<div className="flex justify-content-center align-items-center">
						<Button
							type="button"
							className="p-button-secondary"
							disabled={!hasNext}
							onClick={handleLoadMore}
						>
							{t("license.more")}
						</Button>
					</div>
				)}
				<ConfirmDialog />
			</div>
		</>
	);
};
